import React from 'react';
import { Task } from '../../../types/task';
import { ChatBubbleLeftIcon, BriefcaseIcon, BuildingOfficeIcon, CalendarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export const TASK_STATUSES: { id: Task['status']; label: string; dot: string }[] = [
  { id: 'todo', label: 'Teendő', dot: 'bg-gray-400' },
  { id: 'in_progress', label: 'Folyamatban', dot: 'bg-sky-500' },
  { id: 'completed', label: 'Kész', dot: 'bg-emerald-500' },
  { id: 'cancelled', label: 'Törölve', dot: 'bg-rose-400' }
];

export const PRIORITY_META: Record<Task['priority'], { label: string; dot: string; text: string }> = {
  low: { label: 'Alacsony', dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  medium: { label: 'Közepes', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  high: { label: 'Magas', dot: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400' },
  urgent: { label: 'Sürgős', dot: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400' }
};

export const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

export const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const dueState = (task: Task): 'overdue' | 'today' | 'soon' | 'later' | 'none' => {
  const d = toDate(task.dueDate);
  if (!d) return 'none';
  if (task.status === 'completed' || task.status === 'cancelled') return 'later';
  const today = startOfDay(new Date());
  const day = startOfDay(d);
  const diff = Math.round((day.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 3) return 'soon';
  return 'later';
};

export const fmtDue = (v: any) => {
  const d = toDate(v);
  if (!d) return '';
  const today = startOfDay(new Date());
  const diff = Math.round((startOfDay(d).getTime() - today.getTime()) / 86400000);
  if (diff === 0) return 'Ma';
  if (diff === 1) return 'Holnap';
  if (diff === -1) return 'Tegnap';
  return d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
};

export const initials = (name?: string) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';

/** Kompakt feladatkártya – tábla és naptár nézethez */
export const TaskCard: React.FC<{
  task: Task;
  onClick?: (t: Task) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, t: Task) => void;
  compact?: boolean;
  projectName?: string;
}> = ({ task, onClick, draggable, onDragStart, compact, projectName }) => {
  const pr = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const ds = dueState(task);
  const dueCls = ds === 'overdue' ? 'text-rose-600 dark:text-rose-400' : ds === 'today' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400';
  const comments = task.comments?.length || 0;
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, task)}
      onClick={() => onClick?.(task)}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick?.(task); }}
      className={`crm-card ${compact ? 'px-2 py-1.5' : 'p-3'} cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 transition-colors ${task.status === 'completed' ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${pr.dot}`} title={pr.label} />
        <div className="min-w-0 flex-1">
          <div className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 dark:text-gray-50 ${compact ? 'truncate' : 'line-clamp-2'} ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</div>
          {!compact && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
              <span className="font-mono">{task.taskNumber}</span>
              {task.dueDate && <span className={`inline-flex items-center gap-1 ${dueCls}`}><CalendarIcon className="w-3 h-3" />{fmtDue(task.dueDate)}</span>}
              {projectName && <span className="inline-flex items-center gap-1 truncate max-w-[120px]"><BriefcaseIcon className="w-3 h-3" />{projectName}</span>}
              {task.customerName && <span className="inline-flex items-center gap-1 truncate max-w-[120px]"><BuildingOfficeIcon className="w-3 h-3" />{task.customerName}</span>}
              {comments > 0 && <span className="inline-flex items-center gap-1"><ChatBubbleLeftIcon className="w-3 h-3" />{comments}</span>}
              {(task.subtasks?.length || 0) > 0 && (
                <span className="inline-flex items-center gap-1 tabular-nums"><CheckCircleIcon className="w-3 h-3" />{task.subtasks!.filter(s => s.done).length}/{task.subtasks!.length}</span>
              )}
            </div>
          )}
          {!compact && (task.subtasks?.length || 0) > 0 && (
            <div className="mt-1.5 h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div className="h-full bg-primary-600 rounded-full" style={{ width: `${Math.round((task.subtasks!.filter(s => s.done).length / task.subtasks!.length) * 100)}%` }} />
            </div>
          )}
        </div>
        {task.assignedTo && (
          <span title={task.assignedToName} className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center flex-shrink-0">
            {initials(task.assignedToName)}
          </span>
        )}
      </div>
    </div>
  );
};
